"use client";

import { useEffect, useRef, useState } from "react";
import type { MapHandle, MapMarker, MapMode, MapPlan, MapTrack } from "./types";
import "./map.css";

type Props = {
  /** Kurze Beschreibung für Screenreader, z. B. „Karte: Lage von Playa de Amadores“ */
  label: string;
  markers: MapMarker[];
  tracks?: MapTrack[];
  mode?: MapMode;
  focus?: string;
  zoom?: number;
  pick?: { lat: number; lng: number } | null;
  onPick?: (lat: number, lng: number) => void;
  /** Routenplaner (nur zusammen sinnvoll) */
  plan?: MapPlan;
  fitPlan?: number;
  picking?: boolean;
  onPlanToggle?: (planId: number) => void;
  onMapClick?: (lat: number, lng: number) => void;
  className?: string;
};

/** Leaflet-Karte mit selbst gehosteten Kacheln. Lädt Leaflet erst, wenn die Karte ins Bild kommt. */
export function MapView({ label, markers, tracks, mode = "embed", focus, zoom, pick, onPick, plan, fitPlan, picking, onPlanToggle, onMapClick, className }: Props) {
  const box = useRef<HTMLDivElement>(null);
  const handle = useRef<MapHandle | null>(null);
  const [state, setState] = useState<"wait" | "ready" | "error">("wait");
  const onPickRef = useRef(onPick);
  const pickRef = useRef(pick);
  const planRef = useRef({ plan, picking, onPlanToggle, onMapClick });
  useEffect(() => {
    onPickRef.current = onPick;
    pickRef.current = pick;
    planRef.current = { plan, picking, onPlanToggle, onMapClick };
  });
  const planner = !!onPlanToggle;
  const data = JSON.stringify([markers, tracks, focus, zoom]);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    let alive = true;
    const start = async () => {
      try {
        const { createMap } = await import("./leaflet-map");
        if (!alive) return;
        const [m, t, f, z] = JSON.parse(data) as [MapMarker[], MapTrack[] | undefined, string | undefined, number | undefined];
        const h = createMap(el, {
          mode, markers: m, tracks: t, focus: f, zoom: z, pick: pickRef.current, onPick: (lat, lng) => onPickRef.current?.(lat, lng),
          ...(planner ? {
            onPlanToggle: (id: number) => planRef.current.onPlanToggle?.(id),
            onMapClick: (lat: number, lng: number) => planRef.current.onMapClick?.(lat, lng),
          } : {}),
        });
        handle.current = h;
        const p = planRef.current;
        if (p.plan) h.setPlan(p.plan);
        h.setPicking(!!p.picking);
        setState("ready");
      } catch {
        if (alive) setState("error");
      }
    };
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        io.disconnect();
        void start();
      }
    }, { rootMargin: "300px" });
    io.observe(el);
    return () => {
      alive = false;
      io.disconnect();
      handle.current?.destroy();
      handle.current = null;
    };
  }, [data, mode, planner]);

  const pickLat = pick?.lat;
  const pickLng = pick?.lng;
  useEffect(() => {
    if (mode === "pick") handle.current?.setPick(pickLat != null && pickLng != null ? { lat: pickLat, lng: pickLng } : null);
  }, [mode, pickLat, pickLng]);

  const planData = plan ? JSON.stringify(plan) : "";
  const lastFit = useRef(fitPlan);
  useEffect(() => {
    if (!planData) return;
    const fit = fitPlan !== lastFit.current;
    lastFit.current = fitPlan;
    handle.current?.setPlan(JSON.parse(planData) as MapPlan, fit);
  }, [planData, fitPlan]);
  useEffect(() => {
    handle.current?.setPicking(!!picking);
  }, [picking]);

  return (
    <div className={`gmap gmap-${mode}${className ? ` ${className}` : ""}`} role="region" aria-label={label}>
      <div ref={box} className="gmap-canvas" />
      {state !== "ready" && (
        <p className="gmap-status" aria-live="polite">{state === "error" ? "Die Karte konnte nicht geladen werden." : "Karte wird geladen …"}</p>
      )}
    </div>
  );
}
